import React from "react";
import * as PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import Reference from "../../widgets/Reference";
import ThemeSummaryController from "./ThemeSummaryController";
import Aside from "../../widgets/Aside";
import TextOnly from "../layout/TextOnly";
import calculator from "../../../images/icons/calculator.svg";
import compass from "../../../images/icons/compass.svg";
import downloadPrintReport from "../../../images/icons/downloadPrintReport.svg";
import mousePointer from "../../../images/icons/mouse-pointer.svg";
import styleConstants from "../../../styleConstants";

const styles = {
  container: {
    display: "flex"
  },
  credits: {
    order: 2,
    width: "50%"
  },
  content: {
    order: 1,
    width: "50%",
    padding: "1rem 2rem 0 0",
    "& > p": {
      padding: "0 0 0 2rem"
    }
  },
  contentWide: {
    padding: "0 2rem"
  },
  downloadReportIcon: {
    stroke: styleConstants.colors.primary
  },
  link: {
    color: styleConstants.colors.themeBlue,
    textDecoration: "none",
    borderBottom: `1px ${styleConstants.colors.themeBlue} solid`,
    transition: "color 0.25s ease",
    "&:hover": {
      borderColor: styleConstants.colors.themeBlueMedium,
      color: styleConstants.colors.themeBlueMedium
    }
  },
  summaryIntro: {
    padding: "2rem",
    fontSize: "1.125rem",
    fontWeight: styleConstants.fontWeights.medium,
    color: styleConstants.colors.themeBlue,
    width: "87.5%",
    margin: "2rem 0 0"
  },
  thanks: {
    padding: "1rem 2rem",
    fontSize: "0.875rem",
    marginBottom: "1rem",
    "& > p": {
      paddingBottom: "1rem",
      borderBottom: `1px ${styleConstants.colors.themeBlue} solid`
    }
  },
  thanksTitle: {
    color: styleConstants.colors.themeBlue,
    fontSize: "2rem",
    fontWeight: styleConstants.fontWeights.thin,
    paddingBottom: "1rem"
  }
};

const Introduction = props => (
  <section id="intro">
    <div className={props.classes.container}>
      <div className={props.classes.credits}>
        <Aside icon={calculator}>
          Just want the numbers? Check out our{" "}
          <span className={props.classes.link} onClick={props.onOpenDataAnnex}>
            Data Annex
          </span>
          .
        </Aside>
        <Aside icon={compass}>
          Find data for specific countries in our{" "}
          <span
            className={props.classes.link}
            onClick={props.onOpenCountryExplorer}
          >
            Country Explorer
          </span>
        </Aside>
        <Aside icon={downloadPrintReport}>
          Want to print the report? Download a{" "}
          <a
            href={`${process.env.PUBLIC_URL}/cga_2019_print_version_final.pdf`}
            target="cga_print_report"
          >
            PDF version
          </a>
          .
        </Aside>
        <div className={props.classes.thanks}>
          <h4 className={props.classes.thanksTitle}>Acknowledgements</h4>
          <p>
            The Reproductive Health Supplies Coalition extends its thanks to the
            authors of the Global Contraceptive Commodity Gap Analysis 2019:
            Michelle Weinberger, who also served as the technical lead, Nina
            Miller and John Skibiak. We are grateful to colleagues from DKT,
            PSI, and MSI for sharing not only their data, but, their wealth of
            knowledge and insights which allowed this year’s report to give new
            visibility into the private sector. In particular we would like to
            thank Anisa Berdellima, Nikki Charman, Isabel Duchesne, Danielle
            Garfinkel, Alison Malmqvist, Amy Ratcliffe, Margot Radding, and
            Abigail Winskell. We also express our gratitude to our colleagues at
            FP2020 and to John Stover, Jacqui Darroch, Vladimira Kantorova,
            Philipp Ueffing, Jennie Greaney, and Desmond Koroma for sharing data
            and contributing to our analysis. We thank our partners who have
            turned to the CGA for data to help in their own planning and
            advocacy efforts, including DfID, the Bill and Melinda Gates
            Foundation, the Global Financing Facility, UNFPA, and USAID.
            Finally, we thank Cynthia Breckenridge for her skillful editing,
            layout, and design, which has made this year’s CGA both beautiful
            and accessible, and to Andrew Tait and Bob McKinnon for bringing the
            online report to life.
          </p>
        </div>
        <Aside icon={mousePointer}>
          Many of our charts and tables are interactive.
          <br />
          Dive deeper into the report by selecting different datasets and
          toggling between relative and absolute values.
        </Aside>
      </div>
      <div className={props.classes.content}>
        <p>
          This edition of the Commodity Gap Analysis (CGA 2019) is the fifth in
          a series dating back to 2001. Over the last two decades the report has
          reflected the evolving priorities and questions of our community. The
          first two reports, in 2001 and 2009, focused on donor-supported
          countries and predominantly public sector supplies. More recent
          iterations of the CGA have expanded this scope to cover a wider set of
          countries, and they have looked at users, quantities, and costs in
          both the public and private sectors. These reports have broadened our
          understanding of the wider market; yielded insights from retail data
          acquired from IQVIA, and, helped start conversations about what the
          future might hold as the community looks to meet the Sustainable
          Development Goals.
        </p>
        <p>
          With this edition, we take a new approach. While this CGA presents
          similar findings&mdash;current spending, users, consumption
          quantities, costs and gaps&mdash;it does so in new ways that better
          position these findings to inform the critical discussions happening
          within our community.
        </p>
        <p>
          New analytical approaches allowed us to explore how spending, users,
          and costs vary across not only the public and private sectors, but
          within the private sector as well, offering greater visibility into
          the role of donor-subsidized products in the private sector.
        </p>
        <p>
          This edition also breaks new ground by offering a more nuanced
          understanding of different patterns in spending, use, and cost across
          the 135 low- and middle-income countries (LMIC) comprising our
          analysis.
          <Reference marker={1}>
            The group of 135 low- and middle-income countries excludes China and
            Venezuela, both of which are categorized by the World Bank as
            middle-income.
          </Reference>
          In order to highlight fundamental differences that can help inform
          future planning, we’ve segmented these countries into the World Bank’s
          categories of Gross National Income per Capita (GNI): low-income
          countries (LICs), lower-middle-income countries (L-MICs), and
          upper-middle-income countries (U-MICs).
        </p>
        <p>
          Lastly, we’ve extended the time horizon for this report to 2030 in
          order to help inform pressing conversations about how we plan for the
          future and set out strategies for reaching the Sustainable Development
          Goals (SDGs).
        </p>
      </div>
    </div>
    <div>
      <div className={props.classes.summaryIntro}>
        The rich findings of this report help illuminate pertinent facts about
        the world today, and what changes may happen over the coming decade.
        From these results, five critical themes emerged, themes so important to
        the conversations about the future of our sector that we made a final
        departure from past CGA reports and structured the report itself around
        them. The five themes are:
      </div>
      <ThemeSummaryController
        reference="01"
        title="Reduced or stagnant donor funding in the&nbsp;decade&nbsp;ahead"
      >
        Conversations within the reproductive health community suggest that
        donor support for commodity procurement is declining and will likely
        continue to decline or, at best, remain stagnant in the decade to come.
        In the context of shrinking donor funding for commodities, it is
        important to understand the role donors play in different countries and
        what can be done to minimize the impact of limited funding on those
        women who need it most. Nearly half of spending within low-income
        countries comes from donors, and collectively the public sector accounts
        for 66% of spending. Looking across the three GNI groups, the role of
        the public sector&mdash;namely, the donor community&mdash;diminishes as
        income increases. Prioritizing donor funding amongst lower-income
        countries, therefore, will be critical, as will encouraging country
        governments to play a larger role in funding family planning
        commodities.
      </ThemeSummaryController>

      <ThemeSummaryController
        reference="02"
        title="Distinct method mix across the public and&nbsp;private&nbsp;sectors"
        shift={true}
      >
        Both the public and private sectors play important roles, yet the
        composition of each is very different. While the public sector primarily
        provides long-acting and permanent methods (LAPMs), the private sector
        almost exclusively provides short-term methods (STMs). This means the
        two are not interchangeable. We must take caution in assuming that in
        the context of shrinking donor funding, the private sector can simply
        take the place of what donors are supporting. Work is needed to ensure
        that, as the funding landscape shifts, women are still able to access a
        full range of contraceptive methods.
      </ThemeSummaryController>

      <ThemeSummaryController
        reference="03"
        title="The significant, yet variable, role of subsidy within the&nbsp;private&nbsp;sector"
      >
        The previous CGA sparked debate over the relative role of public sector
        funding within the private sector, specifically as a result of
        subsidized socially marketed products. Building on new data and insights
        shared by DKT, MSI, PSI, and SHOPS Plus, this report distinguishes
        between users who purchase supplies from private sector entities at
        subsidized prices and those who pay non-subsidized (market-value)
        prices. This allows us to present two different financing models: one
        that is purely market driven, the other that relies on support from the
        public sector. The results indicate that across the 135 LMICs, only a
        small share of private sector users (12%) receive subsidized products.
        In terms of the three GNI groups, subsidized private sector commodities
        play the largest proportional role within low-income countries.
        Conversely, non-subsidized private sector commodities play the largest
        role within upper-middle-income countries, where they account for half
        of all users.
      </ThemeSummaryController>

      <ThemeSummaryController
        reference="04"
        title="The differences between the distribution of users and&nbsp;consumption&nbsp;costs"
        shift={true}
      >
        The results of this analysis demonstrate that cost is not a function of
        use. Rather, consumption costs are influenced by a number of factors,
        such as frequency of units consumed for each method, differences in cost
        between methods, and the variation of the cost of each method from
        country to country. Brazil, for example, accounts for only 8% of users,
        yet makes up 30% of the total consumption cost. By contrast, India
        accounts for 30% of all users across the 135 LMIC but makes up only 7%
        of consumption costs. Understanding how the landscapes of use compare to
        those of cost can help ensure all women can make their own choice from a
        full range of contraceptive methods.
      </ThemeSummaryController>

      <ThemeSummaryController
        reference="05"
        title="The possibility of significant, yet&nbsp;uneven,&nbsp;growth"
      >
        Over the coming decade, the total number of women using modern
        contraception will grow by more than 80 million. This growth, however,
        will not be evenly distributed across countries. Low-income countries
        will experience the most rapid growth in relative terms, while
        lower-middle-income countries will experience the largest increase in
        absolute terms. The efforts needed to sustain these different growth
        patterns will vary across countries, with some requiring additional
        money and more effort than others.
      </ThemeSummaryController>
    </div>
    <TextOnly>
      <p>
        Our community is at a pivotal moment: we are on the cusp of reaching
        2020 and beginning to look towards the 2030 Sustainable Development
        Goals. The issues at the center of this analysis are the issues that
        will define the new decade for commodity security: the relative role of
        the public and private sectors; the declining role of donors in
        commodity procurement; the prospects for increased financial support
        from country governments; the impact of subsidized supplies on the total
        market; the evolution of method choice, etc. The conversations and
        decisions over the coming years will shape the future of contraceptive
        use and the landscape of reproductive health supplies. And so, just as
        with the past reports, we hope that this CGA will solicit new questions
        that our next report can seek to answer.
      </p>
    </TextOnly>
  </section>
);

Introduction.propTypes = {
  onOpenCountryExplorer: PropTypes.func.isRequired,
  onOpenDataAnnex: PropTypes.func.isRequired
};

export default withStyles(styles)(Introduction);
